import React, { useMemo, useState, ChangeEvent } from 'react';
import TagInput from './TagInput';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Textarea } from '@/shared/components/ui/Textarea';
import { useCustomersDropdown } from '@/modules/crm/customers/hooks';
import { Icon } from '@/shared/icons'
interface AddContactDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contact: ContactFormData, avatar?: File) => void;
  isLoading?: boolean;
}

export interface ContactFormData {
  name: string;
  identifiers: {
    email: string[];
    phone: string[];
  };
  tags?: string[];
  notes?: string;
  /** Vínculo com `customers` (Plano C). String vazia = "— Sem cliente —". */
  customerId?: string;
  personalInfo?: {
    birthDate?: string;
    /** CPF, CNPJ ou outro documento — texto livre. */
    document?: string;
    address?: {
      street?: string;
      number?: string;
      complement?: string;
      neighborhood?: string;
      city?: string;
      state?: string;
      zipCode?: string;
      country?: string;
    };
  };
}

/**
 * Drawer para adicionar novo contato
 *
 * Suporta multiplos emails, telefones e tags
 * Upload opcional de avatar
 * Abas: Informacoes de Contato + Informacoes Pessoais
 * Validacao: pelo menos 1 email OU 1 telefone obrigatorio
 *
 * @example
 * <AddContactDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSave={handleSaveContact}
 * />
 */
const AddContactDialog: React.FC<AddContactDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  isLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState<'contact' | 'personal'>('contact');
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    identifiers: {
      email: [],
      phone: []
    },
    tags: [],
    notes: '',
    customerId: '',
    personalInfo: {
      address: {
        country: 'Brasil',
      },
    },
  });

  // Dropdown de clientes (Plano C) — drawer só fica montado com `isOpen`,
  // então o fetch já é naturalmente gated (mesmo padrão de useContactsDropdown
  // no ClientContactCard, sem necessidade de `enabled` explícito aqui).
  const { data: customersDropdown, isLoading: isLoadingCustomers } = useCustomersDropdown({ enabled: isOpen });
  const customerOptions = useMemo(
    () => [
      { value: '', label: isLoadingCustomers ? 'Carregando...' : '— Sem cliente —' },
      ...(customersDropdown ?? []).map((c) => ({ value: c.id, label: c.name })),
    ],
    [customersDropdown, isLoadingCustomers]
  );

  const [avatar, setAvatar] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [errors, setErrors] = useState<{
    name?: string;
    avatar?: string;
  }>({});

  const handleNameChange = (value: string) => {
    setFormData((prev) => ({ ...prev, name: value }));
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  const handleEmailsChange = (emails: string[]) => {
    setFormData((prev) => ({
      ...prev,
      identifiers: { ...prev.identifiers, email: emails }
    }));
  };

  const handlePhonesChange = (phones: string[]) => {
    setFormData((prev) => ({
      ...prev,
      identifiers: { ...prev.identifiers, phone: phones }
    }));
  };

  const handleTagsChange = (tags: string[]) => {
    setFormData((prev) => ({ ...prev, tags }));
  };

  const handleCustomerIdChange = (value: string) => {
    setFormData((prev) => ({ ...prev, customerId: value }));
  };

  const handleNotesChange = (value: string) => {
    setFormData((prev) => ({ ...prev, notes: value }));
  };

  const handlePersonalInfoChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        [field]: value,
      },
    }));
  };

  const handleAddressChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        address: {
          ...prev.personalInfo?.address,
          [field]: value,
        },
      },
    }));
  };

  const handleAvatarChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, avatar: 'Por favor, selecione apenas arquivos de imagem' }));
      return;
    }

    // Validar tamanho (5MB maximo)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrors((prev) => ({ ...prev, avatar: 'Arquivo muito grande. O tamanho maximo e 5MB.' }));
      return;
    }

    // Limpar erro anterior
    setErrors((prev) => ({ ...prev, avatar: undefined }));

    // Criar preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    setAvatar(file);
  };

  const handleRemoveAvatar = () => {
    setAvatar(null);
    setAvatarPreview('');
    setErrors((prev) => ({ ...prev, avatar: undefined }));
  };

  const validateEmail = (email: string): string | null => {
    if (!email.trim()) {
      return 'Email nao pode estar vazio';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Email invalido';
    }
    return null;
  };

  const validatePhone = (phone: string): string | null => {
    if (!phone.trim()) {
      return 'Telefone nao pode estar vazio';
    }
    if (!/^[\d\s\-\+\(\)]+$/.test(phone)) {
      return 'Formato de telefone invalido';
    }
    return null;
  };

  const validateForm = (): boolean => {
    const newErrors: {
      name?: string;
      avatar?: string;
    } = {};

    // Nome obrigatorio
    if (!formData.name.trim()) {
      newErrors.name = 'Nome e obrigatorio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Filtrar valores vazios dos arrays
    const cleanedData: ContactFormData = {
      ...formData,
      identifiers: {
        email: formData.identifiers.email.filter(e => e.trim() !== ''),
        phone: formData.identifiers.phone.filter(p => p.trim() !== '')
      },
      tags: formData.tags?.filter(t => t.trim() !== '')
    };

    onSave(cleanedData, avatar || undefined);
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      name: '',
      identifiers: {
        email: [],
        phone: []
      },
      tags: [],
      notes: '',
      customerId: '',
      personalInfo: {
        address: {
          country: 'Brasil',
        },
      },
    });
    setAvatar(null);
    setAvatarPreview('');
    setErrors({});
    setActiveTab('contact');
    onClose();
  };

  const footerContent = (
    <div className="flex items-center justify-end gap-3 w-full">
      <Button
        type="button"
        variant="secondary"
        onClick={handleClose}
        disabled={isLoading}
        data-testid="add-contact-cancel"
      >
        Cancelar
      </Button>
      <Button
        type="submit"
        form="add-contact-form"
        variant="primary"
        loading={isLoading}
        disabled={isLoading}
        data-testid="add-contact-submit"
      >
        {isLoading ? 'Salvando...' : 'Adicionar Contato'}
      </Button>
    </div>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      // `icon` recebe o ícone CRU — o Drawer já o encaixota no chip roxo do
      // `.drw-head`; encaixotar aqui produzia chip dentro de chip.
      icon={<Icon name="user-plus" className="w-5 h-5" />}
      title="Adicionar Contato"
      tabs={{
        items: [
          { id: 'contact', label: 'Informações de Contato' },
          { id: 'personal', label: 'Informações Pessoais' },
        ],
        active: activeTab,
        onChange: (id) => setActiveTab(id as 'contact' | 'personal'),
      }}
      footer={footerContent}
      disabled={isLoading}
    >
      <div className="px-6 py-5">
      <form id="add-contact-form" onSubmit={handleSubmit}>
        {/* Tab: Informacoes de Contato */}
        {activeTab === 'contact' && (
          <div className="space-y-4">
            {/* Avatar Upload */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: '#1e293b' }}
              >
                Foto do Contato
              </label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  {avatarPreview ? (
                    <div className="relative">
                      <img
                        src={avatarPreview}
                        alt="Avatar preview"
                        className="w-20 h-20 rounded-full object-cover border-2"
                        style={{ borderColor: '#e5e7eb' }}
                      />
                      <button
                        type="button"
                        onClick={handleRemoveAvatar}
                        className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        disabled={isLoading}
                      >
                        <Icon name="x" className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div
                      className="w-20 h-20 rounded-full flex items-center justify-center border-2"
                      style={{
                        backgroundColor: '#f1f5f9',
                        borderColor: '#e5e7eb',
                      }}
                    >
                      <Icon name="user" className="w-8 h-8" style={{ color: '#64748b' }} />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    id="avatar-upload"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                    disabled={isLoading}
                  />
                  <label
                    htmlFor="avatar-upload"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg cursor-pointer disabled:opacity-50 transition-colors"
                    style={{
                      color: '#1e293b',
                      backgroundColor: '#ffffff',
                      borderColor: '#e5e7eb',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f5f9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                    }}
                  >
                    <Icon name="image" className="w-4 h-4" />
                    Selecionar Foto
                  </label>
                  <p
                    className="mt-1 text-xs"
                    style={{ color: '#64748b' }}
                  >
                    JPG, PNG ou GIF (max. 5MB)
                  </p>
                </div>
              </div>
              {errors.avatar && (
                <p className="mt-1 text-sm text-red-600">{errors.avatar}</p>
              )}
            </div>

            {/* Nome */}
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: '#1e293b' }}
              >
                Nome <span className="text-red-500">*</span>
              </label>
              <Input
                icon={'user'}
                iconPosition="left"
                type="text"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Nome completo"
                error={errors.name}
                disabled={isLoading}
                data-testid="add-contact-name-input"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Cliente (Plano C — vínculo com customers) */}
            <Select
              label="Cliente"
              options={customerOptions}
              value={formData.customerId || ''}
              onChange={handleCustomerIdChange}
              disabled={isLoading}
              data-testid="contact-customer-select"
            />

            {/* Emails (Tag Input) */}
            <TagInput
              label="Emails"
              values={formData.identifiers.email}
              onChange={handleEmailsChange}
              placeholder="Digite um email e pressione Enter"
              icon={<Icon name="mail" className="w-4 h-4 text-gray-400" />}
              type="email"
              validation={validateEmail}
              disabled={isLoading}
              maxItems={5}
              data-testid="add-contact-emails"
            />

            {/* Telefones (Tag Input) */}
            <TagInput
              label="Telefones"
              values={formData.identifiers.phone}
              onChange={handlePhonesChange}
              placeholder="Digite um telefone e pressione Enter"
              icon={<Icon name="phone" className="w-4 h-4 text-gray-400" />}
              type="tel"
              validation={validatePhone}
              disabled={isLoading}
              maxItems={5}
              data-testid="add-contact-phones"
            />

            {/* Tags (Tag Input) */}
            <TagInput
              label="Tags"
              values={formData.tags || []}
              onChange={handleTagsChange}
              placeholder="Digite uma tag e pressione Enter"
              icon={<Icon name="tag" className="w-4 h-4 text-gray-400" />}
              type="text"
              disabled={isLoading}
              maxItems={10}
              data-testid="add-contact-tags"
            />

            {/* Notas */}
            <Textarea
              label="Notas"
              value={formData.notes}
              onChange={(e) => handleNotesChange(e.target.value)}
              placeholder="Adicione notas..."
              rows={3}
              resize="none"
              disabled={isLoading}
            />
          </div>
        )}

        {/* Tab: Informacoes Pessoais */}
        {activeTab === 'personal' && (
          <div className="space-y-4">
            {/* Data de Nascimento */}
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: '#1e293b' }}
              >
                Data de Nascimento
              </label>
              <Input
                icon={'calendar'}
                iconPosition="left"
                type="date"
                value={formData.personalInfo?.birthDate || ''}
                onChange={(e) => handlePersonalInfoChange('birthDate', e.target.value)}
                disabled={isLoading}
              />
            </div>

            {/* Documento — CPF, CNPJ ou outro, texto livre */}
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: '#1e293b' }}
              >
                Documento (CPF/CNPJ)
              </label>
              <Input
                icon={'file-text'}
                iconPosition="left"
                type="text"
                value={formData.personalInfo?.document || ''}
                onChange={(e) => handlePersonalInfoChange('document', e.target.value)}
                placeholder="CPF, CNPJ ou outro documento"
                maxLength={40}
                disabled={isLoading}
                data-testid="add-contact-document-input"
              />
            </div>

            {/* Endereco - Titulo da secao */}
            <div className="flex items-center gap-2 pt-2">
              <Icon name="map-pin" className="w-4 h-4" style={{ color: '#64748b' }} />
              <span className="text-sm font-medium" style={{ color: '#1e293b' }}>
                Endereço
              </span>
            </div>

            {/* CEP */}
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: '#1e293b' }}
              >
                CEP
              </label>
              <Input
                type="text"
                value={formData.personalInfo?.address?.zipCode || ''}
                onChange={(e) => handleAddressChange('zipCode', e.target.value)}
                placeholder="00000-000"
                disabled={isLoading}
              />
            </div>

            {/* Rua + Numero */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: '#1e293b' }}
                >
                  Endereço
                </label>
                <Input
                  type="text"
                  value={formData.personalInfo?.address?.street || ''}
                  onChange={(e) => handleAddressChange('street', e.target.value)}
                  placeholder="Rua / Logradouro"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: '#1e293b' }}
                >
                  Número
                </label>
                <Input
                  type="text"
                  value={formData.personalInfo?.address?.number || ''}
                  onChange={(e) => handleAddressChange('number', e.target.value)}
                  placeholder="N"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Complemento */}
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: '#1e293b' }}
              >
                Complemento
              </label>
              <Input
                type="text"
                value={formData.personalInfo?.address?.complement || ''}
                onChange={(e) => handleAddressChange('complement', e.target.value)}
                placeholder="Apto, Sala, Bloco..."
                disabled={isLoading}
              />
            </div>

            {/* Bairro */}
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: '#1e293b' }}
              >
                Bairro
              </label>
              <Input
                type="text"
                value={formData.personalInfo?.address?.neighborhood || ''}
                onChange={(e) => handleAddressChange('neighborhood', e.target.value)}
                placeholder="Bairro"
                disabled={isLoading}
              />
            </div>

            {/* Cidade + Estado */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: '#1e293b' }}
                >
                  Cidade
                </label>
                <Input
                  type="text"
                  value={formData.personalInfo?.address?.city || ''}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                  placeholder="Cidade"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: '#1e293b' }}
                >
                  Estado
                </label>
                <Input
                  type="text"
                  value={formData.personalInfo?.address?.state || ''}
                  onChange={(e) => handleAddressChange('state', e.target.value)}
                  placeholder="Estado"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Pais */}
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: '#1e293b' }}
              >
                País
              </label>
              <Input
                type="text"
                value={formData.personalInfo?.address?.country || ''}
                onChange={(e) => handleAddressChange('country', e.target.value)}
                placeholder="País"
                disabled={isLoading}
              />
            </div>
          </div>
        )}
      </form>
      </div>
    </Drawer>
  );
};

export default AddContactDialog;
